(function () {
  'use strict';

  const CACHE_SCHEMA_VERSION = 34;
  const DEFAULT_API_VERSION = '2026-07';
  const DEFAULT_PAGE_SIZE = 50;
  const MAX_NODE_BATCH = 50;

  const INDEX_METAFIELDS = [
    // Existing Wonder Skin product data
    { namespace: 'custom', key: 'hauttyp' },
    { namespace: 'custom', key: 'hautprobleme' },
    { namespace: 'custom', key: 'produkttyp' },
    { namespace: 'custom', key: 'inhaltsstoffe' },
    { namespace: 'custom', key: 'inhaltsstoffe_produkt' },
    { namespace: 'custom', key: 'vegan' },
    { namespace: 'custom', key: 'cruelty_free' },
    { namespace: 'custom', key: 'other_tags' },
    { namespace: 'custom', key: 'other_flags' },
    { namespace: 'custom', key: 'land' },

    // Explicit quiz attributes. When filled, these override automatic inference.
    { namespace: 'custom', key: 'quiz_skin_types' },
    { namespace: 'custom', key: 'quiz_concerns' },
    { namespace: 'custom', key: 'quiz_steps' },
    { namespace: 'custom', key: 'quiz_daypart' },
    { namespace: 'custom', key: 'quiz_actives' },
    { namespace: 'custom', key: 'quiz_strength' },
    { namespace: 'custom', key: 'quiz_sensitive' },
    { namespace: 'custom', key: 'quiz_retinoid_compatible' },
    { namespace: 'custom', key: 'quiz_acid_compatible' },
    { namespace: 'custom', key: 'quiz_budget_level' },
    { namespace: 'custom', key: 'quiz_priority' },
    { namespace: 'custom', key: 'quiz_professional_profile' },
    { namespace: 'custom', key: 'routine_visual' },
    { namespace: 'custom', key: 'anwendung_produkt' },

    // Professional routine-balance attributes. Optional: the engine infers
    // them from current product data until curated metafields are supplied.
    { namespace: 'custom', key: 'quiz_irritation_load' },
    { namespace: 'custom', key: 'quiz_moisturizer_weight' },
    { namespace: 'custom', key: 'quiz_spf_usage' },
    { namespace: 'custom', key: 'quiz_cleanser_intensity' },

    { namespace: 'reviews', key: 'rating' },
    { namespace: 'custom', key: 'bewertung' },
    { namespace: 'reviews', key: 'rating_count' }
  ];

  const DETAIL_METAFIELDS = [
    { namespace: 'custom', key: 'routine_visual' },
    { namespace: 'custom', key: 'anwendung_produkt' },
    { namespace: 'custom', key: 'short_text' },
    { namespace: 'custom', key: 'vorteile_ideal' },
    { namespace: 'custom', key: 'weil' },
    { namespace: 'custom', key: 'faq_produkt' }
  ];

  class StorefrontApiError extends Error {
    constructor(message, details) {
      super(message);
      this.name = 'StorefrontApiError';
      this.details = details;
    }
  }

  class StorefrontClient {
    constructor(config) {
      this.domain = String(config.domain || '').replace(/^https?:\/\//, '').replace(/\/$/, '');
      this.token = String(config.token || '').trim();
      this.apiVersion = String(config.apiVersion || DEFAULT_API_VERSION).trim();
      this.timeoutMs = Number(config.timeoutMs || 15000);

      if (!this.domain) throw new Error('Missing Shopify store domain.');
      if (!this.token) throw new Error('Missing public Storefront API token.');
    }

    get endpoint() {
      return `https://${this.domain}/api/${this.apiVersion}/graphql.json`;
    }

    async request(query, variables, options) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), (options && options.timeoutMs) || this.timeoutMs);

      try {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.token
          },
          body: JSON.stringify({ query, variables: variables || {} }),
          signal: controller.signal
        });

        const payload = await response.json().catch(() => null);

        if (!response.ok) {
          throw new StorefrontApiError(`Storefront API HTTP ${response.status}`, payload);
        }

        if (!payload) {
          throw new StorefrontApiError('Storefront API returned an empty or invalid response.', null);
        }

        if (payload.errors && payload.errors.length) {
          const firstMessage = payload.errors[0] && payload.errors[0].message ? payload.errors[0].message : 'Unknown GraphQL error';
          throw new StorefrontApiError(`Storefront API GraphQL error: ${firstMessage}`, payload.errors);
        }

        return payload.data;
      } catch (error) {
        if (error && error.name === 'AbortError') {
          throw new StorefrontApiError('Storefront API request timed out.', null);
        }
        throw error;
      } finally {
        clearTimeout(timeout);
      }
    }
  }

  function safeJson(value, fallback) {
    if (value === null || value === undefined || value === '') return fallback;
    try { return JSON.parse(value); } catch (_) { return fallback; }
  }

  function parseListMetafield(value) {
    const parsed = safeJson(value, null);
    if (Array.isArray(parsed)) return parsed.map(String).map(v => v.trim()).filter(Boolean);
    if (typeof value !== 'string') return [];
    return value.split(',').map(v => v.trim()).filter(Boolean);
  }

  function parseRating(value) {
    const parsed = safeJson(value, null);
    if (parsed && parsed.value !== undefined) return Number(parsed.value) || 0;
    return Number(value) || 0;
  }

  function parseBooleanMetafield(value) {
    if (typeof value === 'boolean') return value;
    const normalized = String(value || '').trim().toLowerCase();
    return ['true', '1', 'yes', 'ja', 'y'].includes(normalized);
  }

  function parseJsonObject(value) {
    const raw = String(value || '').trim();
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    } catch (_) { return null; }
  }

  function firstNonEmpty() {
    for (const value of arguments) {
      if (value !== null && value !== undefined && String(value).trim() !== '') return value;
    }
    return '';
  }

  function richTextToPlainText(value) {
    const root = safeJson(value, null);
    if (!root || typeof root !== 'object') return String(value || '').trim();
    const chunks = [];
    const walk = node => {
      if (!node) return;
      if (typeof node.value === 'string') chunks.push(node.value);
      if (Array.isArray(node.children)) node.children.forEach(walk);
    };
    walk(root);
    return chunks.join(' ').replace(/\s+/g, ' ').trim();
  }

  function metafieldMap(metafields) {
    const map = {};
    (metafields || []).filter(Boolean).forEach(item => {
      map[`${item.namespace}.${item.key}`] = item;
    });
    return map;
  }

  function normalizeText(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9+%]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function uniq(values) {
    return Array.from(new Set((values || []).filter(Boolean)));
  }

  function handleFromGid(gid) {
    return String(gid || '').split('/').pop() || '';
  }


  function parseOptionalBoolean(value) {
    const raw = String(value == null ? '' : value).trim().toLowerCase();
    if (!raw) return null;
    if (['true', '1', 'yes', 'ja', 'y'].includes(raw)) return true;
    if (['false', '0', 'no', 'nein', 'n'].includes(raw)) return false;
    return null;
  }

  function parseFlexibleList(value) {
    if (value == null || value === '') return [];
    const parsed = parseListMetafield(value);
    if (parsed.length) return parsed;
    return String(value)
      .split(/[,;|]/)
      .map(item => item.trim())
      .filter(Boolean);
  }

  function normalizeQuizStep(value) {
    const v = normalizeText(value).replace(/_/g, ' ');
    if (/oil cleanser|cleansing oil|reinigungsol|olreiniger|cleansing balm|reinigungsbalsam/.test(v)) return 'oil_cleanser';
    if (/cleanser|reiniger|reinigung|face wash|cleansing foam|cleansing gel/.test(v)) return 'cleanser';
    if (/toner|mist/.test(v)) return 'toner';
    if (/essence|essenz/.test(v)) return 'essence';
    if (/serum|ampoule|ampulle|treatment|booster/.test(v)) return 'treatment';
    if (/moisturizer|cream|creme|lotion|emulsion|feuchtigkeit/.test(v)) return 'moisturizer';
    if (/spf|sunscreen|sonnenschutz|sun cream|sun serum/.test(v)) return 'sunscreen';
    return String(value || '').trim().toLowerCase().replace(/\s+/g, '_');
  }

  function normalizeQuizDaypart(value) {
    const v = normalizeText(value);
    if (/am pm|am\/pm|morgen.*abend|morning.*evening|beide|both/.test(v)) return 'AM/PM';
    if (/^am$|morgen|morning|day/.test(v)) return 'AM';
    if (/^pm$|abend|night|evening/.test(v)) return 'PM';
    return '';
  }

  function normalizeQuizStrength(value) {
    const v = normalizeText(value);
    if (/advanced|fortgeschritten|stark|intensiv/.test(v)) return 'advanced';
    if (/intermediate|mittel|erfahren/.test(v)) return 'intermediate';
    if (/beginner|anfanger|mild|sanft/.test(v)) return 'beginner';
    return '';
  }

  function inferStrength(product) {
    const risk = Math.max(Number(product.irritationRisk || 0), Number(product.advancedTreatmentRisk || 0));
    if (risk >= 3) return 'advanced';
    if (risk >= 1) return 'intermediate';
    return 'beginner';
  }

  function inferDayparts(product) {
    if (product.role === 'sunscreen') return ['AM'];
    if (product.role === 'oil_cleanser') return ['PM'];
    const active = new Set(product.activeFamilies || []);
    if (active.has('retinoid') || active.has('aha') || active.has('bha') || active.has('pha')) return ['PM'];
    return ['AM', 'PM'];
  }

  function inferBudgetLevel(product) {
    const price = product && product.variant && product.variant.price
      ? Number(product.variant.price.amount || 0)
      : 0;
    if (!price) return 'unknown';
    if (price <= 18) return 'value';
    if (price <= 30) return 'standard';
    if (price <= 50) return 'premium';
    return 'luxury';
  }


  function normalizeMoisturizerWeight(value) {
    const v = normalizeText(value);
    if (/rich|reichhaltig|barrier|balm|balsam|intensive/.test(v)) return 'rich';
    if (/light|leicht|gel|lotion|emulsion/.test(v)) return 'light';
    if (/balanced|ausgewogen|medium|normal/.test(v)) return 'balanced';
    return '';
  }

  function normalizeSpfUsage(value) {
    const v = normalizeText(value);
    if (/reapplication|reapply|nachcremen|stick|cushion|pact|powder|puder|mist|spray/.test(v)) return 'reapplication';
    if (/primary|main|basis|morning|morgens/.test(v)) return 'primary';
    return '';
  }

  function normalizeCleanserIntensity(value) {
    const v = normalizeText(value);
    if (/deep|pore|soda|scrub|peel|exfoliat|intensiv|stark/.test(v)) return 'deep';
    if (/gentle|mild|sanft|barrier|amino|low ph/.test(v)) return 'gentle';
    if (/standard|normal|balanced|ausgewogen/.test(v)) return 'standard';
    return '';
  }

  function buildQuizAttributes(product, get) {
    const explicitSkin = parseFlexibleList((get('custom', 'quiz_skin_types') || {}).value).map(value => {
      const raw = normalizeText(value);
      const aliases = {
        'dry': 'dry', 'trocken': 'dry', 'trockene haut': 'dry',
        'combination': 'combination', 'mischhaut': 'combination',
        'oily': 'oily', 'fettig': 'oily', 'fettige haut': 'oily',
        'normal': 'normal', 'normale haut': 'normal',
        'sensitive': 'sensitive', 'empfindlich': 'sensitive', 'empfindliche haut': 'sensitive',
        'mature': 'mature', 'reife haut': 'mature',
        'all': 'all', 'alle hauttypen': 'all'
      };
      return aliases[raw] || raw.replace(/\s+/g, '_');
    });

    const explicitConcerns = parseFlexibleList((get('custom', 'quiz_concerns') || {}).value).map(value => {
      const raw = normalizeText(value);
      const aliases = {
        'acne': 'blemishes', 'blemishes': 'blemishes', 'unreinheiten': 'blemishes',
        'pores': 'pores', 'poren': 'pores',
        'pigmentation': 'pigmentation', 'pigmentflecken': 'pigmentation',
        'redness': 'redness', 'rotungen': 'redness',
        'aging': 'anti_age', 'anti aging': 'anti_age', 'slow aging': 'anti_age',
        'lines': 'lines', 'falten': 'lines',
        'firmness': 'firmness', 'festigkeit': 'firmness', 'straffung': 'firmness', 'hauterschlaffung': 'firmness',
        'dehydration': 'dryness', 'dryness': 'dryness', 'trockenheit': 'dryness',
        'sensitivity': 'sensitivity', 'sensibilitat': 'sensitivity', 'barrierepflege': 'sensitivity',
        'dullness': 'dullness', 'fahler teint': 'dullness',
        'dark circles': 'dark_circles', 'augenringe': 'dark_circles'
      };
      return aliases[raw] || raw.replace(/\s+/g, '_');
    });

    const explicitSteps = parseFlexibleList((get('custom', 'quiz_steps') || {}).value)
      .map(normalizeQuizStep).filter(Boolean);
    const explicitDayparts = parseFlexibleList((get('custom', 'quiz_daypart') || {}).value)
      .map(normalizeQuizDaypart).filter(Boolean);
    const explicitActives = parseFlexibleList((get('custom', 'quiz_actives') || {}).value)
      .map(value => normalizeText(value).replace(/\s+/g, '_')).filter(Boolean);
    const explicitStrength = normalizeQuizStrength((get('custom', 'quiz_strength') || {}).value);
    const explicitSensitive = parseOptionalBoolean((get('custom', 'quiz_sensitive') || {}).value);
    const explicitRetinoidCompatibility = parseOptionalBoolean((get('custom', 'quiz_retinoid_compatible') || {}).value);
    const explicitAcidCompatibility = parseOptionalBoolean((get('custom', 'quiz_acid_compatible') || {}).value);
    const explicitBudget = normalizeText((get('custom', 'quiz_budget_level') || {}).value).replace(/\s+/g, '_');
    const explicitPriorityRaw = Number((get('custom', 'quiz_priority') || {}).value);
    const explicitPriority = Number.isFinite(explicitPriorityRaw) ? Math.max(0, Math.min(10, explicitPriorityRaw)) : 0;
    const explicitIrritationRaw = Number((get('custom', 'quiz_irritation_load') || {}).value);
    const explicitIrritationLoad = Number.isFinite(explicitIrritationRaw)
      ? Math.max(0, Math.min(5, explicitIrritationRaw))
      : null;
    const explicitMoisturizerWeight = normalizeMoisturizerWeight((get('custom', 'quiz_moisturizer_weight') || {}).value);
    const explicitSpfUsage = normalizeSpfUsage((get('custom', 'quiz_spf_usage') || {}).value);
    const explicitCleanserIntensity = normalizeCleanserIntensity((get('custom', 'quiz_cleanser_intensity') || {}).value);

    const activeSet = new Set(explicitActives.length ? explicitActives : product.activeFamilies || []);
    const fallbackSensitive = product.skinTypes.includes('sensitive')
      ? true
      : (Number(product.irritationRisk || 0) >= 2 ? false : null);

    return {
      skinTypes: explicitSkin.length ? uniq(explicitSkin) : product.skinTypes.slice(),
      concerns: explicitConcerns.length ? uniq(explicitConcerns) : product.concerns.slice(),
      steps: explicitSteps.length ? uniq(explicitSteps) : [product.role],
      dayparts: explicitDayparts.length
        ? uniq(explicitDayparts.flatMap(value => value === 'AM/PM' ? ['AM', 'PM'] : [value]))
        : inferDayparts(product),
      actives: explicitActives.length ? uniq(explicitActives) : product.activeFamilies.slice(),
      strength: explicitStrength || inferStrength(product),
      sensitive: explicitSensitive !== null ? explicitSensitive : fallbackSensitive,
      retinoidCompatible: explicitRetinoidCompatibility !== null
        ? explicitRetinoidCompatibility
        : !(activeSet.has('aha') || activeSet.has('bha') || activeSet.has('pha') || activeSet.has('benzoyl_peroxide')),
      acidCompatible: explicitAcidCompatibility !== null
        ? explicitAcidCompatibility
        : !activeSet.has('retinoid'),
      budgetLevel: explicitBudget || inferBudgetLevel(product),
      priorityScore: explicitPriority,
      irritationLoad: explicitIrritationLoad,
      moisturizerWeight: explicitMoisturizerWeight,
      spfUsage: explicitSpfUsage,
      cleanserIntensity: explicitCleanserIntensity,
      source: {
        skinTypes: explicitSkin.length ? 'metafield' : 'legacy',
        concerns: explicitConcerns.length ? 'metafield' : 'legacy',
        steps: explicitSteps.length ? 'metafield' : 'inferred',
        dayparts: explicitDayparts.length ? 'metafield' : 'inferred',
        actives: explicitActives.length ? 'metafield' : 'inferred',
        strength: explicitStrength ? 'metafield' : 'inferred',
        sensitive: explicitSensitive !== null ? 'metafield' : 'inferred',
        compatibility: (explicitRetinoidCompatibility !== null || explicitAcidCompatibility !== null) ? 'metafield' : 'inferred',
        budgetLevel: explicitBudget ? 'metafield' : 'price',
        priorityScore: explicitPriorityRaw ? 'metafield' : 'default',
        irritationLoad: explicitIrritationLoad !== null ? 'metafield' : 'inferred',
        moisturizerWeight: explicitMoisturizerWeight ? 'metafield' : 'inferred',
        spfUsage: explicitSpfUsage ? 'metafield' : 'inferred',
        cleanserIntensity: explicitCleanserIntensity ? 'metafield' : 'inferred'
      }
    };
  }

  function normalizeProduct(node) {
    const fields = metafieldMap(node.metafields);
    const get = (namespace, key) => fields[`${namespace}.${key}`] || null;
    const list = (namespace, key) => parseListMetafield((get(namespace, key) || {}).value);
    const scalar = (namespace, key) => String(((get(namespace, key) || {}).value) || '').trim();

    const skinTypesRaw = list('custom', 'hauttyp');
    const concernsRaw = list('custom', 'hautprobleme');
    const productTypesRaw = list('custom', 'produkttyp');
    const ingredientsRaw = list('custom', 'inhaltsstoffe');
    const inci = scalar('custom', 'inhaltsstoffe_produkt');
    const routineVisual = scalar('custom', 'routine_visual');
    const usage = scalar('custom', 'anwendung_produkt');
    const explicitProfile = parseJsonObject(scalar('custom', 'quiz_professional_profile'));
    const otherTags = firstNonEmpty(scalar('custom', 'other_tags'), scalar('custom', 'other_flags'));
    const classificationText = normalizeText([
      node.title,
      node.handle,
      node.productType,
      productTypesRaw.join(' '),
      otherTags
    ].join(' '));
    const allSearchableText = normalizeText([
      classificationText,
      ingredientsRaw.join(' '),
      inci,
      routineVisual,
      usage
    ].join(' '));

    const normalized = {
      id: node.id,
      numericId: handleFromGid(node.id),
      handle: node.handle,
      title: node.title,
      vendor: node.vendor || '',
      productType: node.productType || '',
      tags: [],
      updatedAt: node.updatedAt,
      availableForSale: Boolean(node.availableForSale),
      onlineStoreUrl: node.onlineStoreUrl || '',
      image: node.featuredImage || null,
      variant: node.selectedOrFirstAvailableVariant || null,
      priceRange: node.priceRange || null,
      skinTypesRaw,
      concernsRaw,
      productTypesRaw,
      ingredientsRaw,
      inci,
      routineVisual,
      usage,
      explicitProfessionalProfile: explicitProfile,
      vegan: parseBooleanMetafield(scalar('custom', 'vegan')) || /vegan/i.test(scalar('custom', 'vegan')) || productTypesRaw.some(v => /vegan/i.test(v)),
      crueltyFree: parseBooleanMetafield(scalar('custom', 'cruelty_free')) || /cruelty/i.test(scalar('custom', 'cruelty_free')) || productTypesRaw.some(v => /cruelty/i.test(v)),
      otherTags,
      country: list('custom', 'land'),
      rating: parseRating(firstNonEmpty((get('reviews', 'rating') || {}).value, (get('custom', 'bewertung') || {}).value)),
      ratingCount: Number((get('reviews', 'rating_count') || {}).value) || 0,
      classificationText,
      searchableText: allSearchableText
    };

    normalized.bundleStatus = deriveBundleStatus(normalized);
    normalized.role = explicitProfile && explicitProfile.role ? explicitProfile.role : deriveRoutineRole(normalized);
    normalized.format = deriveFormat(normalized);
    normalized.skinTypes = deriveSkinTypes(skinTypesRaw);
    normalized.concerns = deriveConcerns(concernsRaw);
    normalized.activeFamilies = deriveActiveFamilies(normalized);
    normalized.declaredActiveFamilies = deriveDeclaredActiveFamilies(normalized);
    normalized.supportFamilies = deriveSupportFamilies(normalized);
    normalized.advancedTreatmentRisk = deriveAdvancedTreatmentRisk(normalized);
    normalized.irritationRisk = deriveIrritationRisk(normalized);
    normalized.fragranceStatus = deriveFragranceStatus(normalized);
    normalized.professional = explicitProfile ? explicitProfessionalIntelligence(normalized, explicitProfile) : buildProfessionalIntelligence(normalized);
    normalized.eligibility = deriveEligibility(normalized);
    normalized.quiz = buildQuizAttributes(normalized, get);
    if (explicitProfile) {
      // v6.3.0 professional profile is the authoritative quiz data source.
      // Historical standalone custom.quiz_* fields may still exist in Shopify
      // from older deployments; they must not override or contaminate the
      // current professional profile.
      const profileSkinTypes = deriveSkinTypes(Array.isArray(explicitProfile.skin_types) ? explicitProfile.skin_types : []);
      const profileConcerns = deriveConcerns(Array.isArray(explicitProfile.concerns) ? explicitProfile.concerns : []);
      if (profileSkinTypes.length) normalized.quiz.skinTypes = profileSkinTypes;
      if (profileConcerns.length || Array.isArray(explicitProfile.concerns)) normalized.quiz.concerns = profileConcerns;
      normalized.quiz.steps = [normalized.role];

      const dp = explicitProfile.daypart_policy || {};
      const explicitParts = [];
      if (dp.am_allowed) explicitParts.push('AM');
      if (dp.pm_allowed) explicitParts.push('PM');
      if (explicitParts.length) normalized.quiz.dayparts = explicitParts;
      if (Array.isArray(explicitProfile.declared_actives)) normalized.quiz.actives = uniq(explicitProfile.declared_actives);
      if (explicitProfile.strength) normalized.quiz.strength = explicitProfile.strength;
      if (explicitProfile.sensitive_suitability === 'high') normalized.quiz.sensitive = true;
      else if (explicitProfile.sensitive_suitability === 'low') normalized.quiz.sensitive = false;
      else normalized.quiz.sensitive = null;
      if (Number.isFinite(Number(explicitProfile.irritation_load))) normalized.quiz.irritationLoad = Number(explicitProfile.irritation_load);
      // Compatibility defaults are intentionally permissive here. Product-level
      // treatment safety is governed by the explicit professional profile; stale
      // legacy compatibility booleans must not silently block otherwise valid v6.2
      // combinations.
      normalized.quiz.retinoidCompatible = true;
      normalized.quiz.acidCompatible = true;
      const rs = explicitProfile.role_specific || {};
      normalized.quiz.moisturizerWeight = rs.weight || '';
      normalized.quiz.cleanserIntensity = rs.cleansing_intensity || '';
      if (normalized.role === 'sunscreen') normalized.quiz.spfUsage = rs.primary_spf === false ? 'reapplication' : 'primary';
      normalized.quiz.sources = Object.assign({}, normalized.quiz.sources || {}, { professionalProfile: 'metafield-v6.3.0' });
    }

    return normalized;
  }

  function deriveBundleStatus(product) {
    const typeText = normalizeText((product.productTypesRaw || []).join(' '));
    const titleText = normalizeText([product.title, product.handle, product.productType].filter(Boolean).join(' '));

    const explicitType = /(^|\s)(set|kit|bundle|routine set|ritual set|geschenkset|gift set|collection)(\s|$)/.test(typeText);
    const handleSignal = /(^|[-_])(set|kit|bundle)([-_]|$)/.test(String(product.handle || '').toLowerCase());
    const titleSignal = /\b(set|kit|bundle|geschenkset|gift set|routine set|ritual set)\b/.test(titleText);
    const multiProductSignal = /\b([4-9]|[1-9][0-9])\s*(teilig|piece|pieces|produkte|products|schritte|steps)\b/.test(titleText);

    return {
      isBundle: Boolean(explicitType || handleSignal || titleSignal || multiProductSignal),
      source: explicitType ? 'product_type' : handleSignal ? 'handle' : titleSignal ? 'title' : multiProductSignal ? 'title_count' : 'none'
    };
  }

  function deriveRoutineRole(product) {
    const t = product.classificationText || '';
    const typeText = normalizeText((product.productTypesRaw || []).join(' '));
    const has = pattern => pattern.test(t);
    const typeHas = pattern => pattern.test(typeText);

    // Product identity must be decided before ingredient/function inference.
    if (product.bundleStatus && product.bundleStatus.isBundle) return 'bundle';
    if (has(/geschenkgutschein|gutschein|testprodukt|pauschalrucksendung/)) return 'excluded';

    // Existing custom.produkttyp is the strongest live catalog signal. It is
    // still dynamic Shopify data, not a quiz-specific manual product list.
    if (typeHas(/olreiniger|oil cleanser|cleansing oil|cleansing balm/)) return 'oil_cleanser';
    if (typeHas(/(^|\s)cleanser(\s|$)|reiniger|reinigung/)) return 'cleanser';
    if (typeHas(/sonnenschutz|sunscreen|sun cream|spf/)) return 'sunscreen';
    if (/spray serum|serum spray|mist serum|serum mist/.test(t)) return 'toner';
    if (/multi balm|multibalm/.test(t)) return 'unknown';
    // Dosage form wins over marketing-layer labels when Shopify stores more
    // than one Produkttyp. A Serum Mist is a support mist, not a treatment
    // anchor; a Cream Mist is not a primary moisturizer.
    if (typeHas(/cushion|pact|foundation|bb cream|cc cream/)) {
      return /sonnenschutz|sunscreen|sun cream|spf|uv/.test(t) ? 'sunscreen' : 'makeup';
    }
    if (typeHas(/mist|spray/)) return 'toner';
    if (typeHas(/toner/)) return 'toner';
    if (typeHas(/essenz|essence/)) return 'essence';
    // Mixed Serum + Cream products stay treatment-like for display, but format
    // intelligence below prevents them from becoming a therapeutic anchor.
    if (typeHas(/serum|ampulle|ampoule|treatment|booster/)) return 'treatment';
    if (typeHas(/creme|cream|moisturizer|lotion|emulsion/)) return 'moisturizer';

    if (has(/haar|shampoo|conditioner|hair mask|scalp/)) return 'hair';
    if (has(/cushion|pact/) && has(/spf\s?\d|lsf\s?\d|sunscreen|sun cream|uv/)) return 'sunscreen';
    if (has(/foundation|mascara|lippenstift|eyeliner|make up|makeup|cushion|pact/)) return 'makeup';
    if (has(/olreiniger|cleansing oil|reinigungsol|cleansing balm|reinigungsbalsam/)) return 'oil_cleanser';
    if (has(/spot patch|acne patch|pickel patch|blemish patch/)) return 'spot_treatment';
    if (has(/augencreme|eye cream|augenpflege|eye serum|eye patch/)) return 'eye_care';
    if (has(/peeling|exfoliant|scrub|aha toner|bha toner|peeling pad|exfoliating pad/)) return 'exfoliant';
    if (has(/maske|mask sheet|sheet mask|sleeping mask|wash off mask|rubber mask/)) return 'mask';
    if (has(/sonnenschutz|sunscreen|sun cream|spf\s?\d|lsf\s?\d|sun stick|sun serum|uv cream|uv gel/)) return 'sunscreen';
    if (has(/toner|lotion toner|skin softener/)) return 'toner';
    if (has(/\bmist\b|face mist|cream mist|facial mist|skin mist|pflege mist|gesichtsspray|face spray|facial spray/)) return 'toner';
    // In K-beauty naming, "essence cream" / "serum cream" are cream formats,
    // not essence/serum treatment slots. Final dosage-form noun wins here.
    if (has(/essence cream|essenz creme|essenz cream|serum cream|serum creme|ampoule cream|ampoule creme/)) return 'moisturizer';
    if (has(/essenz|essence/)) return 'essence';
    if (has(/serum|ampulle|ampoule|treatment|booster|concentrate|konzentrat/)) return 'treatment';
    if (has(/gesichtsol|face oil|facial oil/)) return 'face_oil';
    // Cleanser identity outranks incidental words such as "Reinigungsemulsion".
    if (has(/reiniger|cleanser|cleansing foam|cleansing gel|waschgel|reinigungsschaum|reinigungspuder|face wash/)) return 'cleanser';
    if (has(/creme|cream|moisturizer|feuchtigkeitscreme|gel cream|gel creme|lotion|emulsion/)) return 'moisturizer';
    return 'unknown';
  }

  function deriveFormat(product) {
    if (product.bundleStatus && product.bundleStatus.isBundle) return 'set';
    const t = product.classificationText || '';
    const typeText = normalizeText((product.productTypesRaw || []).join(' '));
    if (/cushion|pact/.test(typeText)) return 'cushion';
    if (/mist|spray/.test(typeText)) return 'toner';
    if (/multi balm|multibalm/.test(t)) return 'balm';
    if (/(essence cream|essenz creme|essenz cream|serum cream|serum creme|ampoule cream|ampoule creme)/.test(t)) return 'cream';
    if (/(serum.*cream|cream.*serum|serum.*creme|creme.*serum)/.test(t)) return 'hybrid_serum_cream';
    if (/(spray serum|serum spray|mist serum|serum mist)/.test(t)) return 'spray_serum';
    if (/sun stick|sunstick|\bstick\b/.test(t)) return 'stick';
    if (/cushion|pact/.test(t)) return 'cushion';
    if (/\bampoule\b|ampulle|ampule|ampulla/.test(t)) return 'ampoule';
    if (/\bserum\b|sun serum/.test(t)) return 'serum';
    if (/essenz|essence/.test(t)) return 'essence';
    if (/toner|mist|spray/.test(t)) return 'toner';
    if (/cream|creme|moisturizer|gel cream|gel creme/.test(t)) return 'cream';
    if (/lotion|emulsion/.test(t)) return 'lotion';
    if (/cleansing oil|reinigungsol|olreiniger/.test(t)) return 'oil';
    if (/balm|balsam/.test(t)) return 'balm';
    if (/foam|schaum/.test(t)) return 'foam';
    if (/gel/.test(t)) return 'gel';
    if (/powder|puder/.test(t)) return 'powder';
    return product.role || 'unknown';
  }

  function deriveSkinTypes(values) {
    const map = {
      'alle hauttypen': 'all',
      'empfindliche haut': 'sensitive',
      'trockene haut': 'dry',
      'mischhaut': 'combination',
      'normale haut': 'normal',
      'reife haut': 'mature',
      'fettige haut': 'oily'
    };
    return uniq(values.map(v => map[normalizeText(v)] || normalizeText(v).replace(/ /g, '_')));
  }

  function deriveConcerns(values) {
    const found = [];

    (values || []).forEach(value => {
      const v = normalizeText(value);
      if (!v) return;

      const add = concern => { if (concern) found.push(concern); };

      if (/^(trockenheit|dehydrierung|feuchtigkeitsmangel|feuchtigkeitsarme haut|trockene dehydrierte haut)$/.test(v)) add('dryness');
      else if (/^(unreinheiten|unreine haut|pickelanfallige haut|akne|akne und unreinheiten|pickel mitesser)$/.test(v)) add('blemishes');
      else if (/^(grosse poren|sichtbare poren|poren|erweiterte poren)$/.test(v)) add('pores');
      else if (/^(pigmentflecken|pigmentierungen|hyperpigmentierung|hyperpigmentation|pickelmale|post inflammatorische hyperpigmentierung)$/.test(v)) add('pigmentation');
      else if (/^(rotungen|rotungen und entzundungen|entzundungen|gerotete haut)$/.test(v)) add('redness');
      else if (/^(fahler teint|ungleichmassiger hautton und glanzlose haut|ungleichmassiger hautton|glanzlose haut|muder teint)$/.test(v)) add('dullness');
      else if (/^(anti aging|anti ageing|skin longevity|slow aging)$/.test(v)) add('anti_age');
      else if (/^(falten linien|feine linien und falten|feine linien|falten)$/.test(v)) add('lines');
      else if (/^(hauterschlaffung|straffung|festigkeitsverlust|elastizitatsverlust|weniger festigkeit)$/.test(v)) add('firmness');
      else if (/^(sensible haut|empfindliche haut|sensibilitat|reaktive haut)$/.test(v)) add('sensitivity');
      else if (/^(augenschatten|augenringe|dunkle augenringe)$/.test(v)) add('dark_circles');
      else if (/^(barrierepflege|barrierestorung|hautbarrierestorung|hautbarriere gestort|geschwachte hautbarriere|barriere geschwacht)$/.test(v)) {
        add('sensitivity');
        add('dryness');
      } else {
        // Preserve unknown values for diagnostics without forcing them into a
        // known scoring bucket.
        add(v.replace(/ /g, '_'));
      }
    });

    return uniq(found);
  }

  function extractActiveFamilies(text) {
    const t = text || '';
    const found = [];
    const add = (name, pattern) => { if (pattern.test(t)) found.push(name); };

    add('retinoid', /\bretinol\b|\bretinal\b|retinaldehyde|retinyl palmitate|retinyl retinoate|retinyl propionate|retinyl acetate|hydroxypinacolone retinoate|granactive retinoid|retinoid/);
    add('bha', /salicyl|betaine salicylate|\bbha\b|capryloyl salicylic|\blha\b/);
    add('aha', /glycolic acid|glycolic|lactic acid|mandelic acid|mandelic|\baha\b/);
    add('pha', /gluconolactone|lactobionic|\bpha\b/);
    add('vitamin_c', /ascorbic acid|ascorbyl|vitamin c|vita c|vit c|3 o ethyl ascorbic/);
    add('azelaic', /azelaic|azelainsaure/);
    add('niacinamide', /niacinamide|nicotinamide/);
    add('tranexamic', /tranexamic|\btxa\b/);
    add('arbutin', /arbutin/);
    add('kojic', /kojic/);
    add('benzoyl_peroxide', /benzoyl peroxide/);
    add('sulfur', /\bsulfur\b|schwefel/);
    add('bakuchiol', /bakuchiol/);
    return uniq(found);
  }

  function deriveActiveFamilies(product) {
    // Full INCI remains available for safety / incompatibility checks.
    return extractActiveFamilies(product.searchableText || '');
  }

  function deriveDeclaredActiveFamilies(product) {
    // Therapeutic evidence must distinguish product identity / declared tags
    // from an ingredient merely occurring somewhere in the INCI.
    return extractActiveFamilies(product.classificationText || '');
  }

  function deriveSupportFamilies(product) {
    const t = product.searchableText;
    const found = [];
    const add = (name, pattern) => { if (pattern.test(t)) found.push(name); };

    add('ceramides', /ceramide/);
    add('panthenol', /panthenol|provitamin b5/);
    add('centella', /centella|cica|madecassoside|asiaticoside/);
    add('heartleaf', /heartleaf|houttuynia cordata|houttuynia/);
    add('mugwort', /mugwort|artemisia|beifuss/);
    add('hyaluronic', /hyaluronic|hyaluron/);
    add('glycerin', /glycerin|glycerol/);
    add('squalane', /squalane|squalan/);
    add('peptides', /peptide/);
    add('pdrn', /\bpdrn\b|polydeoxyribonucleotide|polydeoxyribonucleotid|salmon dna|salmon-derived dna/);
    add('collagen', /collagen|kollagen/);
    add('allantoin', /allantoin/);
    add('beta_glucan', /beta glucan|β glucan/);
    add('ectoin', /ectoin/);
    add('urea', /\burea\b|harnstoff/);
    add('cholesterol', /cholesterol/);
    add('rice', /\brice\b|\breis\b|reisextrakt|rice extract|oryza sativa|rice water/);
    add('probiotics', /probiotic|probiotisch|bifida|lactobacillus|galactomyces|fermentiert|fermented|ferment filtrate|ferment lysate/);
    add('snail_mucin', /snail mucin|snail secretion|schneckenschleim|snail filtrate/);
    add('green_tea', /green tea|gruner tee|camellia sinensis/);
    add('tea_tree', /tea tree|melaleuca alternifolia/);
    add('licorice', /licorice|liquorice|sussholz|glycyrrhiza/);
    add('glutathione', /glutathione|glutathion/);
    add('tremella', /tremella|snow mushroom|silberohr|silberohrpilz/);
    add('aloe', /aloe vera|aloe barbadensis/);
    add('propolis', /propolis/);
    add('ginseng', /ginseng|panax ginseng/);
    add('q10', /coenzyme q10|coenzym q10|ubiquinone|ubiquinol|\bq10\b/);
    add('tocopherol', /tocopherol|vitamin e/);
    return uniq(found);
  }

  function deriveProfessionalStrategyProfile(product) {
    const active = new Set(product.activeFamilies || []);
    const declared = new Set(product.declaredActiveFamilies || []);
    const support = new Set(product.supportFamilies || []);
    const concerns = new Set(product.concerns || []);
    const identity = product.classificationText || '';
    const therapyEvidence = {};
    const supportEvidence = {};
    const bump = (target, name, value) => {
      if (!name || !value) return;
      target[name] = Math.min(3, Math.max(Number(target[name] || 0), Number(value || 0)));
    };
    const t = (name, value) => bump(therapyEvidence, name, value);
    const s = (name, value) => bump(supportEvidence, name, value);
    const isDeclared = name => declared.has(name);
    const has = name => active.has(name);

    // Hautprobleme is contextual intent, never sufficient on its own to make a
    // serum/essence a strong treatment for that concern.
    if (concerns.has('blemishes')) t('blemish_control', 0.7);
    if (concerns.has('pores')) t('pore_refinement', 0.7);
    if (concerns.has('pigmentation')) t('pigment_correction', 0.7);
    if (concerns.has('redness')) t('redness_support', 0.7);
    if (concerns.has('dryness')) t('hydration', 0.7);
    if (concerns.has('dullness')) t('glow', 0.7);
    if (concerns.has('anti_age') || concerns.has('lines') || concerns.has('firmness')) t('anti_age', 0.7);
    if (concerns.has('sensitivity')) { t('redness_support', 0.8); t('barrier_support', 0.8); }

    // Declared/title/type actives are strong therapeutic evidence. INCI-only
    // presence is weak corroboration and cannot turn an unrelated product into
    // a pore/acne/pigment treatment.
    if (has('azelaic')) {
      if (isDeclared('azelaic')) { t('blemish_control', 3); t('pigment_correction', 2.8); t('redness_support', 2.4); }
      else {
        if (concerns.has('blemishes')) t('blemish_control', 1.15);
        if (concerns.has('pigmentation')) t('pigment_correction', 1.15);
        if (concerns.has('redness')) t('redness_support', 1.05);
      }
    }
    if (has('bha')) {
      if (isDeclared('bha')) { t('pore_refinement', 3); t('sebum_control', 2.8); t('blemish_control', 2.5); t('texture_refinement', 2.8); }
      else {
        if (concerns.has('pores')) { t('pore_refinement', 1.15); t('texture_refinement', 1.0); }
        if (concerns.has('blemishes')) t('blemish_control', 1.05);
      }
    }
    if (has('aha') || has('pha')) {
      const declaredAcid = isDeclared('aha') || isDeclared('pha');
      if (declaredAcid) {
        t('texture_refinement', 2.8);
        if (concerns.has('pores')) t('pore_refinement', 2.0);
        if (concerns.has('dullness')) t('glow', 2.0);
        if (concerns.has('pigmentation')) t('pigment_correction', 1.7);
      } else {
        if (concerns.has('pores')) t('texture_refinement', 1.0);
        if (concerns.has('dullness')) t('glow', 0.9);
        if (concerns.has('pigmentation')) t('pigment_correction', 0.9);
      }
    }
    if (has('retinoid') || has('bakuchiol')) {
      const declaredRetinoid = isDeclared('retinoid') || isDeclared('bakuchiol');
      if (declaredRetinoid) {
        t('anti_age', isDeclared('retinoid') ? 3 : 2.4);
        t('texture_refinement', isDeclared('retinoid') ? 2.7 : 2.0);
        if (concerns.has('blemishes')) t('blemish_control', 1.8);
        if (concerns.has('pigmentation')) t('pigment_correction', 1.8);
        if (concerns.has('pores')) t('pore_refinement', 1.7);
      } else if (concerns.has('anti_age') || concerns.has('lines') || concerns.has('firmness')) {
        t('anti_age', 1.15);
      }
    }
    if (has('tranexamic') || has('arbutin') || has('vitamin_c') || has('kojic')) {
      const declaredBrightener = ['tranexamic','arbutin','vitamin_c','kojic'].some(isDeclared);
      if (declaredBrightener) { t('pigment_correction', 3); t('glow', 2.5); }
      else if (concerns.has('pigmentation') || concerns.has('dullness')) {
        t('pigment_correction', 1.15); t('glow', 1.0);
      }
    }
    if (has('benzoyl_peroxide') || has('sulfur')) {
      const declaredBlemish = isDeclared('benzoyl_peroxide') || isDeclared('sulfur');
      if (declaredBlemish) t('blemish_control', 3);
      else if (concerns.has('blemishes')) t('blemish_control', 1.15);
    }

    if (has('niacinamide')) {
      const factor = isDeclared('niacinamide') ? 1 : 0.55;
      if (concerns.has('pores')) { t('pore_refinement', 1.8 * factor); t('sebum_control', 1.6 * factor); }
      if (concerns.has('blemishes')) t('blemish_control', 1.5 * factor);
      if (concerns.has('pigmentation')) t('pigment_correction', 1.8 * factor);
      if (concerns.has('dullness')) t('glow', 1.6 * factor);
      if (concerns.has('anti_age') || concerns.has('lines')) s('anti_age', 1.0);
    }

    const hasAny = names => names.some(name => support.has(name));
    const hydrationSupport = hasAny(['hyaluronic','glycerin','beta_glucan','panthenol','urea','tremella','aloe','squalane']);
    const barrierSupport = hasAny(['ceramides','cholesterol','squalane','panthenol','ectoin','centella','beta_glucan']);
    const calmingSupport = hasAny(['centella','heartleaf','mugwort','panthenol','beta_glucan','ectoin','allantoin','licorice']);
    const glowSupport = hasAny(['rice','licorice','glutathione','probiotics','green_tea']);
    const agingSupport = hasAny(['peptides','pdrn','collagen','ginseng','q10']);

    // Supportive INCI never promotes itself into therapeutic evidence.
    if (hydrationSupport) s('hydration', 2.0);
    if (barrierSupport) s('barrier_support', 2.0);
    if (calmingSupport) s('redness_support', 2.0);
    if (glowSupport) s('glow', 1.7);
    if (agingSupport) s('anti_age', 2.0);

    // Product identity is stronger than INCI. These signals are deliberately
    // restricted to title/type/tags (classificationText), not ingredients.
    if (/dark spot|brighten|brightening|mela|melano|tone up|tone-up|whitening|glutathione/.test(identity)) {
      t('pigment_correction', Math.max(2.0, therapyEvidence.pigment_correction || 0));
      t('glow', Math.max(1.8, therapyEvidence.glow || 0));
    }
    if (/pore|blackhead|sebum/.test(identity)) {
      t('pore_refinement', Math.max(2.2, therapyEvidence.pore_refinement || 0));
      t('sebum_control', Math.max(1.8, therapyEvidence.sebum_control || 0));
    }
    if (/acne|blemish|clear skin|a-clear|pimple|breakout|trouble care|spot treatment|acne spot/.test(identity)) t('blemish_control', Math.max(2.2, therapyEvidence.blemish_control || 0));
    if (/calm|calming|soothing|cica|centella|heartleaf|madeca|relief/.test(identity)) {
      t('redness_support', Math.max(1.9, therapyEvidence.redness_support || 0));
      s('barrier_support', Math.max(1.5, supportEvidence.barrier_support || 0));
    }
    if (/hydrat|moisture|aqua|waterful|waterfull|ceramide|barrier|repair/.test(identity)) {
      t('hydration', Math.max(1.8, therapyEvidence.hydration || 0));
      s('barrier_support', Math.max(1.5, supportEvidence.barrier_support || 0));
    }
    if (/retinol|retinal|retino|wrinkle|firming|lifting|collagen|pdrn|peptide/.test(identity)) t('anti_age', Math.max(2.0, therapyEvidence.anti_age || 0));

    // Concern tags remain support context and can help non-anchor roles.
    if (concerns.has('blemishes')) s('blemish_control', 0.8);
    if (concerns.has('pores')) s('pore_refinement', 0.8);
    if (concerns.has('pigmentation')) s('pigment_correction', 0.8);
    if (concerns.has('redness') || concerns.has('sensitivity')) s('redness_support', Math.max(1.0, supportEvidence.redness_support || 0));
    if (concerns.has('dryness')) s('hydration', Math.max(1.0, supportEvidence.hydration || 0));
    if (concerns.has('dullness')) s('glow', Math.max(1.0, supportEvidence.glow || 0));
    if (concerns.has('anti_age') || concerns.has('lines') || concerns.has('firmness')) s('anti_age', Math.max(1.0, supportEvidence.anti_age || 0));

    const therapeutic = Object.keys(therapyEvidence).filter(name => therapyEvidence[name] >= 0.7);
    const supportive = Object.keys(supportEvidence).filter(name => supportEvidence[name] >= 0.7);
    return {
      therapeutic: uniq(therapeutic), supportive: uniq(supportive), therapyEvidence, supportEvidence,
      declaredActives: uniq(Array.from(declared)), inciOnlyActives: uniq(Array.from(active).filter(name => !declared.has(name)))
    };
  }

  function deriveProfessionalStrategies(product) {
    const profile = deriveProfessionalStrategyProfile(product);
    return uniq(profile.therapeutic.concat(profile.supportive));
  }

  function deriveProfessionalIntensity(product) {
    const risk = Math.max(Number(product.irritationRisk || 0), Number(product.advancedTreatmentRisk || 0));
    const active = new Set(product.activeFamilies || []);
    const declared = new Set(product.declaredActiveFamilies || []);
    const identity = product.classificationText || '';
    const concentration = pattern => {
      const match = identity.match(pattern);
      return match ? Number(match[1] || 0) : 0;
    };
    const azelaicPct = declared.has('azelaic') ? concentration(/azelaic(?:\s+acid)?\s*(\d{1,2}(?:\.\d+)?)\s*%?/) : 0;
    const niacinamidePct = declared.has('niacinamide') ? concentration(/niacinamid(?:e)?\s*(\d{1,2}(?:\.\d+)?)\s*%?/) : 0;
    const vitaminCPct = declared.has('vitamin_c') ? concentration(/vitamin\s*c\s*(\d{1,2}(?:\.\d+)?)\s*%?/) : 0;

    if (risk >= 3.5 || product.advancedTreatmentRisk >= 3) return 'specialist';
    if (risk >= 2.5 || active.has('retinoid') || active.has('benzoyl_peroxide') || vitaminCPct >= 20) return 'strong';
    if (risk >= 1.2 || active.has('aha') || active.has('bha') || active.has('pha') || azelaicPct >= 10 || niacinamidePct >= 10 || vitaminCPct >= 10) return 'moderate';
    return 'gentle';
  }

  function explicitProfessionalIntelligence(product, rawProfile) {
    const q = rawProfile || {};
    // v6.3.0 top-level fields are authoritative. Some profiles retain a nested
    // legacy professional block from v6.1 for compatibility; never let that
    // stale block overwrite the rebuilt v6.2 values.
    const legacy = q.professional || {};
    const evidence = Object.assign({}, legacy.strategyEvidence || {}, q.therapeutic_evidence || {});
    const support = Object.assign({}, legacy.supportEvidence || {}, q.support_evidence || {});
    const roleSpecific = Object.assign({}, legacy.roleSpecific || {}, q.role_specific || {});
    const daypartPolicy = Object.assign({}, legacy.daypartPolicy || {}, q.daypart_policy || {});
    const therapeutic = Object.keys(evidence).filter(name => Number(evidence[name] || 0) >= 0.7);
    const supportive = Object.keys(support).filter(name => Number(support[name] || 0) >= 0.7);
    const maxEvidence = Math.max(0, ...Object.values(evidence).map(Number));
    const format = product.format || deriveFormat(product);
    const primarySpf = product.role === 'sunscreen' ? roleSpecific.primary_spf !== false : false;
    return {
      isBundle: false,
      format,
      strategies: uniq(therapeutic.concat(supportive)),
      therapeuticStrategies: therapeutic,
      supportStrategies: supportive,
      strategyEvidence: evidence,
      supportEvidence: support,
      declaredActives: uniq(q.declared_actives || legacy.declaredActives || []),
      detectedActives: uniq(q.detected_actives || legacy.detectedActives || q.declared_actives || []),
      activeConcentrations: Object.assign({}, legacy.activeConcentrations || {}, q.active_concentrations || {}),
      intensity: q.strength || legacy.intensity || 'gentle',
      primaryTarget: String((q.clinical_profile || {}).primary_target || legacy.primaryTarget || '').toLowerCase(),
      anchorEligible: typeof q.anchor_eligible === 'boolean'
        ? q.anchor_eligible
        : (product.role === 'treatment' && ['serum', 'ampoule'].includes(format) && maxEvidence >= 1.5),
      therapeuticEssenceEligible: false,
      primarySpf,
      spfLevel: Number.isFinite(Number(roleSpecific.spf_level)) ? Number(roleSpecific.spf_level) : null,
      reapplicationSpf: product.role === 'sunscreen' && !primarySpf,
      qualityTier: q.quality_tier || legacy.qualityTier || 'standard',
      priority: Number(q.priority != null ? q.priority : (legacy.priority || 0)),
      sensitiveSuitability: q.sensitive_suitability || legacy.sensitiveSuitability || 'conditional',
      barrierCompatibility: q.barrier_compatibility || legacy.barrierCompatibility || 'medium',
      irritationLoad: Number.isFinite(Number(q.irritation_load)) ? Number(q.irritation_load) : (Number.isFinite(Number(legacy.irritationLoad)) ? Number(legacy.irritationLoad) : null),
      cautions: uniq(q.cautions || legacy.cautions || []),
      roleSpecific,
      daypartPolicy,
      source: q.source || legacy.source || 'explicit',
      profileVersion: q.version || legacy.profileVersion || null
    };
  }

  function buildProfessionalIntelligence(product) {
    const strategyProfile = deriveProfessionalStrategyProfile(product);
    const strategies = uniq(strategyProfile.therapeutic.concat(strategyProfile.supportive));
    const format = product.format || deriveFormat(product);
    const isBundle = Boolean(product.bundleStatus && product.bundleStatus.isBundle);
    const singleTherapeuticFormat = ['serum', 'ampoule'].includes(format);
    const therapeuticEssence = format === 'essence' && strategyProfile.therapeutic.length > 0;
    const essenceIdentity = product.classificationText || '';
    const essenceExplicitTreatmentClaim = /(treatment essence|active essence|brighten|dark spot|pore|acne|blemish|retinol|retinal|wrinkle|firming|lifting|intensive hydrat|barrier repair)/.test(essenceIdentity);
    const essenceDeclaredActive = (product.declaredActiveFamilies || []).length > 0;
    const spfMatch = (product.classificationText || '').match(/(?:spf|lsf)\s*([0-9]{2,3})/);
    const spfLevel = spfMatch ? Number(spfMatch[1]) : null;
    const makeupSpf = /\bbb cream\b|\bcc cream\b|foundation/.test(product.classificationText || '');
    const spraySpf = /\bmist\b|\bspray\b|gesichtsspray|face spray|facial spray/.test(product.classificationText || '');
    const reapplicationFormat = ['stick', 'cushion', 'powder'].includes(format) || spraySpf;
    const explicitlyReapplication = product.spfUsage === 'reapplication';
    const explicitlyPrimary = product.spfUsage === 'primary';
    const primarySpf = product.role === 'sunscreen' && !makeupSpf && !reapplicationFormat && !explicitlyReapplication;

    return {
      isBundle,
      format,
      strategies,
      therapeuticStrategies: strategyProfile.therapeutic,
      supportStrategies: strategyProfile.supportive,
      strategyEvidence: strategyProfile.therapyEvidence,
      supportEvidence: strategyProfile.supportEvidence,
      declaredActives: strategyProfile.declaredActives,
      inciOnlyActives: strategyProfile.inciOnlyActives,
      intensity: deriveProfessionalIntensity(product),
      anchorEligible: !isBundle && product.role === 'treatment' && singleTherapeuticFormat && Math.max(0, ...Object.values(strategyProfile.therapyEvidence || {})) >= 1.5,
      therapeuticEssenceEligible: !isBundle && product.role === 'essence' && therapeuticEssence && essenceExplicitTreatmentClaim && essenceDeclaredActive && Math.max(0, ...Object.values(strategyProfile.therapyEvidence || {})) >= 2.2,
      primarySpf,
      spfLevel,
      reapplicationSpf: product.role === 'sunscreen' && (reapplicationFormat || explicitlyReapplication) && !explicitlyPrimary,
      classificationSource: product.bundleStatus && product.bundleStatus.source ? product.bundleStatus.source : 'derived'
    };
  }

  function deriveAdvancedTreatmentRisk(product) {
    const t = product.searchableText || '';
    let risk = 0;

    // Spicule / "needle" style cosmetic treatments need their own risk signal.
    // They are not reliably detected by the chemical-active parser above.
    if (/reedle|spicule|spicules|microspicule|micro spicule|microneedle|micro needle|needle shot/.test(t)) {
      risk = Math.max(risk, 3);
    }

    // VT-style Reedle strength tiers. Keep 50 accessible with caution, but
    // treat 100+ as advanced for a beginner-facing recommendation engine.
    if (/reedle shot\s*50\b/.test(t)) risk = Math.max(risk, 2);
    if (/reedle shot\s*100\b/.test(t)) risk = Math.max(risk, 3);
    if (/reedle shot\s*300\b/.test(t)) risk = Math.max(risk, 4);
    if (/reedle shot\s*(700|1000)\b/.test(t)) risk = Math.max(risk, 5);

    if (/professional peel|strong peel|peeling solution/.test(t)) {
      risk = Math.max(risk, 3);
    }

    // High-intensity "shot" delivery products used for pore/texture routines
    // should contribute to the routine burden even when "reedle/spicule" is
    // not written in the title.
    if (/exosome shot|pore serum\s*(2000|7500)|shot pore serum/.test(t)) {
      risk = Math.max(risk, 3);
    }

    return Math.min(5, risk);
  }

  function deriveIrritationRisk(product) {
    let risk = 0;
    const actives = new Set(product.activeFamilies);
    if (actives.has('retinoid')) risk += 3;
    if (actives.has('aha')) risk += 2;
    if (actives.has('bha')) risk += 2;
    if (actives.has('pha')) risk += 1;
    if (actives.has('benzoyl_peroxide')) risk += 3;
    if (product.role === 'exfoliant') risk += 1;
    if (product.role === 'cleanser') risk = Math.max(0, risk - 1);

    // Advanced mechanical/spicule technologies are a separate source of burden.
    risk = Math.max(risk, Number(product.advancedTreatmentRisk || 0));
    return Math.min(5, risk);
  }

  function deriveFragranceStatus(product) {
    const t = product.searchableText;
    if (/fragrance free|fragrance-free|parfumfrei|ohne duftstoffe|duftstofffrei/.test(t)) return 'free';
    if (/\bparfum\b|\bfragrance\b|essential oil|limonene|linalool|citral|geraniol|eugenol/.test(t)) return 'present_or_allergens';
    return 'unknown';
  }

  function deriveEligibility(product) {
    const reasons = [];
    const coreAndOptionalRoles = new Set([
      'oil_cleanser', 'cleanser', 'toner', 'essence', 'treatment', 'moisturizer',
      'sunscreen', 'exfoliant', 'mask', 'eye_care', 'spot_treatment', 'face_oil'
    ]);

    if (!product.onlineStoreUrl) reasons.push('not_published_to_online_store');
    if (!coreAndOptionalRoles.has(product.role)) reasons.push(`unsupported_role:${product.role}`);
    if (!product.skinTypes.length && !product.concerns.length && !product.ingredientsRaw.length) reasons.push('insufficient_quiz_data');
    if (!product.variant || !product.variant.id) reasons.push('missing_variant');

    return {
      eligible: reasons.length === 0,
      reasons
    };
  }

  function buildIndexSelection() {
    return `
      id
      handle
      title
      vendor
      productType
      updatedAt
      availableForSale
      onlineStoreUrl
      featuredImage { url altText width height }
      priceRange { minVariantPrice { amount currencyCode } maxVariantPrice { amount currencyCode } }
      selectedOrFirstAvailableVariant {
        id
        title
        availableForSale
        quantityAvailable

        price { amount currencyCode }
        compareAtPrice { amount currencyCode }
        image { url altText width height }
      }
      metafields(identifiers: $identifiers) { namespace key type value }
    `;
  }

  const QUERY_PRODUCTS_INDEX = `#graphql
    query SkinQuizProducts($first: Int!, $after: String, $identifiers: [HasMetafieldsIdentifier!]!) {
      products(first: $first, after: $after, sortKey: ID) {
        pageInfo { hasNextPage endCursor }
        nodes { ${buildIndexSelection()} }
      }
    }
  `;

  const QUERY_COLLECTION_INDEX = `#graphql
    query SkinQuizCollectionProducts($handle: String!, $first: Int!, $after: String, $identifiers: [HasMetafieldsIdentifier!]!) {
      collection(handle: $handle) {
        id
        products(first: $first, after: $after) {
          pageInfo { hasNextPage endCursor }
          nodes { ${buildIndexSelection()} }
        }
      }
    }
  `;

  const QUERY_PRODUCTS_MANIFEST = `#graphql
    query SkinQuizManifest($first: Int!, $after: String) {
      products(first: $first, after: $after, sortKey: ID) {
        pageInfo { hasNextPage endCursor }
        nodes { id updatedAt availableForSale onlineStoreUrl }
      }
    }
  `;

  const QUERY_COLLECTION_MANIFEST = `#graphql
    query SkinQuizCollectionManifest($handle: String!, $first: Int!, $after: String) {
      collection(handle: $handle) {
        id
        products(first: $first, after: $after) {
          pageInfo { hasNextPage endCursor }
          nodes { id updatedAt availableForSale onlineStoreUrl }
        }
      }
    }
  `;

  const QUERY_PRODUCTS_BY_IDS = `#graphql
    query SkinQuizProductsByIds($ids: [ID!]!, $identifiers: [HasMetafieldsIdentifier!]!) {
      nodes(ids: $ids) {
        ... on Product { ${buildIndexSelection()} }
      }
    }
  `;

  const QUERY_PRODUCT_DETAILS = `#graphql
    query SkinQuizProductDetails($ids: [ID!]!, $identifiers: [HasMetafieldsIdentifier!]!) {
      nodes(ids: $ids) {
        ... on Product {
          id
          handle
          title
          updatedAt
          availableForSale
          onlineStoreUrl
          featuredImage { url altText width height }
          selectedOrFirstAvailableVariant {
            id
            title
            availableForSale
            quantityAvailable

            price { amount currencyCode }
            compareAtPrice { amount currencyCode }
            image { url altText width height }
          }
          metafields(identifiers: $identifiers) { namespace key type value }
        }
      }
    }
  `;

  class CatalogService {
    constructor(config) {
      this.client = new StorefrontClient(config);
      this.collectionHandle = String(config.collectionHandle || '').trim();
      this.pageSize = Math.min(250, Math.max(20, Number(config.pageSize || DEFAULT_PAGE_SIZE)));
      this.cacheMinutes = Math.max(1, Number(config.cacheMinutes || 15));
      this.debug = Boolean(config.debug);
      const fieldSignature = INDEX_METAFIELDS.map(item => `${item.namespace}.${item.key}`).join(',');
      this.cacheKey = `skin-quiz-catalog:v${CACHE_SCHEMA_VERSION}:${this.client.apiVersion}:${this.client.domain}:${this.collectionHandle || 'all'}:${fieldSignature}`;
    }

    log() {
      if (this.debug && window.console) console.info('[SkinQuizCatalog]', ...arguments);
    }

    readCache() {
      try {
        const raw = localStorage.getItem(this.cacheKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || parsed.schemaVersion !== CACHE_SCHEMA_VERSION || !Array.isArray(parsed.products)) return null;
        return parsed;
      } catch (_) {
        return null;
      }
    }

    writeCache(products) {
      const payload = {
        schemaVersion: CACHE_SCHEMA_VERSION,
        savedAt: new Date().toISOString(),
        products
      };
      try { localStorage.setItem(this.cacheKey, JSON.stringify(payload)); }
      catch (error) { this.log('Cache write failed', error); }
      return payload;
    }

    isCacheFresh(cache) {
      if (!cache || !cache.savedAt) return false;
      const age = Date.now() - new Date(cache.savedAt).getTime();
      return Number.isFinite(age) && age < this.cacheMinutes * 60 * 1000;
    }

    hasUsableMetafieldCoverage(products) {
      const sample = (products || []).slice(0, 100);
      if (!sample.length) return false;
      const withQuizData = sample.filter(product =>
        (product.skinTypesRaw && product.skinTypesRaw.length) ||
        (product.concernsRaw && product.concernsRaw.length) ||
        (product.productTypesRaw && product.productTypesRaw.length) ||
        (product.ingredientsRaw && product.ingredientsRaw.length) ||
        product.inci
      ).length;
      return withQuizData / sample.length >= 0.15;
    }

    async load(options) {
      const onProgress = options && typeof options.onProgress === 'function' ? options.onProgress : function () {};
      const cached = this.readCache();

      if (!cached || !this.hasUsableMetafieldCoverage(cached.products)) {
        const products = await this.fullSync(onProgress);
        return { products, source: cached ? 'network-recovery' : 'network', refreshPromise: Promise.resolve(products) };
      }

      const refreshPromise = this.syncIncremental(cached.products, onProgress).catch(error => {
        this.log('Background refresh failed; using cache.', error);
        return cached.products;
      });

      if (this.isCacheFresh(cached)) {
        return { products: cached.products, source: 'fresh-cache', refreshPromise };
      }

      const freshProducts = await refreshPromise;
      return { products: freshProducts, source: 'refreshed-cache', refreshPromise: Promise.resolve(freshProducts) };
    }

    async fullSync(onProgress) {
      const products = [];
      let after = null;
      let page = 0;

      do {
        page += 1;
        onProgress({ phase: 'catalog', page, loaded: products.length });
        const variables = {
          first: this.pageSize,
          after,
          identifiers: INDEX_METAFIELDS
        };
        if (this.collectionHandle) variables.handle = this.collectionHandle;

        const data = await this.client.request(
          this.collectionHandle ? QUERY_COLLECTION_INDEX : QUERY_PRODUCTS_INDEX,
          variables
        );
        const connection = this.collectionHandle ? data.collection && data.collection.products : data.products;
        if (!connection) throw new StorefrontApiError('Quiz collection was not found or is not accessible.', this.collectionHandle);

        connection.nodes.filter(Boolean).forEach(node => products.push(normalizeProduct(node)));
        after = connection.pageInfo.hasNextPage ? connection.pageInfo.endCursor : null;
      } while (after);

      this.writeCache(products);
      onProgress({ phase: 'complete', loaded: products.length });
      return products;
    }

    async fetchManifest(onProgress) {
      const manifest = [];
      let after = null;
      let page = 0;

      do {
        page += 1;
        onProgress({ phase: 'manifest', page, loaded: manifest.length });
        const variables = { first: 250, after };
        if (this.collectionHandle) variables.handle = this.collectionHandle;
        const data = await this.client.request(
          this.collectionHandle ? QUERY_COLLECTION_MANIFEST : QUERY_PRODUCTS_MANIFEST,
          variables
        );
        const connection = this.collectionHandle ? data.collection && data.collection.products : data.products;
        if (!connection) throw new StorefrontApiError('Quiz collection was not found or is not accessible.', this.collectionHandle);
        connection.nodes.filter(Boolean).forEach(node => manifest.push(node));
        after = connection.pageInfo.hasNextPage ? connection.pageInfo.endCursor : null;
      } while (after);

      return manifest;
    }

    async fetchProductsByIds(ids, onProgress) {
      const normalized = [];
      for (let start = 0; start < ids.length; start += MAX_NODE_BATCH) {
        const batch = ids.slice(start, start + MAX_NODE_BATCH);
        onProgress({ phase: 'changes', loaded: start, total: ids.length });
        const data = await this.client.request(QUERY_PRODUCTS_BY_IDS, {
          ids: batch,
          identifiers: INDEX_METAFIELDS
        });
        (data.nodes || []).filter(Boolean).forEach(node => normalized.push(normalizeProduct(node)));
      }
      return normalized;
    }

    async syncIncremental(cachedProducts, onProgress) {
      const manifest = await this.fetchManifest(onProgress);
      const cachedById = new Map(cachedProducts.map(product => [product.id, product]));
      const changedIds = [];

      manifest.forEach(item => {
        const cached = cachedById.get(item.id);
        if (!cached || cached.updatedAt !== item.updatedAt || cached.availableForSale !== Boolean(item.availableForSale)) {
          changedIds.push(item.id);
        }
      });

      const changedProducts = changedIds.length ? await this.fetchProductsByIds(changedIds, onProgress) : [];
      const changedById = new Map(changedProducts.map(product => [product.id, product]));
      const merged = [];

      manifest.forEach(item => {
        const current = changedById.get(item.id) || cachedById.get(item.id);
        if (current && item.onlineStoreUrl) merged.push(current);
      });

      this.writeCache(merged);
      const manifestIds = new Set(manifest.map(product => product.id));
      const removedCount = cachedProducts.filter(product => !manifestIds.has(product.id)).length;
      const addedCount = manifest.filter(product => !cachedById.has(product.id)).length;
      onProgress({
        phase: 'complete',
        loaded: merged.length,
        changed: changedIds.length,
        added: addedCount,
        removed: removedCount
      });
      return merged;
    }

    async getFreshDetails(ids) {
      const uniqueIds = uniq(ids).slice(0, 20);
      if (!uniqueIds.length) return [];
      const data = await this.client.request(QUERY_PRODUCT_DETAILS, {
        ids: uniqueIds,
        identifiers: DETAIL_METAFIELDS
      });

      return (data.nodes || []).filter(Boolean).map(node => {
        const fields = metafieldMap(node.metafields);
        const value = (namespace, key) => String(((fields[`${namespace}.${key}`] || {}).value) || '').trim();
        return {
          id: node.id,
          handle: node.handle,
          title: node.title,
          updatedAt: node.updatedAt,
          availableForSale: Boolean(node.availableForSale),
          onlineStoreUrl: node.onlineStoreUrl,
          image: node.featuredImage,
          variant: node.selectedOrFirstAvailableVariant,
          routineVisual: value('custom', 'routine_visual'),
          usage: value('custom', 'anwendung_produkt'),
          shortText: richTextToPlainText(value('custom', 'short_text')),
          benefits: richTextToPlainText(value('custom', 'vorteile_ideal')),
          whySelected: richTextToPlainText(value('custom', 'weil')),
          faq: richTextToPlainText(value('custom', 'faq_produkt')),
          ingredientCards: [],
          complementaryProducts: []
        };
      });
    }

    clearCache() {
      try { localStorage.removeItem(this.cacheKey); } catch (_) {}
    }
  }

  window.SkinQuizCatalog = {
    CatalogService,
    StorefrontClient,
    StorefrontApiError,
    normalizeProduct,
    parseListMetafield,
    parseBooleanMetafield,
    deriveAdvancedTreatmentRisk,
    deriveBundleStatus,
    deriveRoutineRole,
    deriveFormat,
    buildProfessionalIntelligence,
    richTextToPlainText,
    constants: {
      CACHE_SCHEMA_VERSION,
      DEFAULT_API_VERSION,
      INDEX_METAFIELDS,
      DETAIL_METAFIELDS
    }
  };
})();
